// third-party
import React from 'react';
import { GlyphInfo, GlyphLinkOut, Tooltip, TooltipButton } from '@americanexpress/dls-react';

// components
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles and icons
import styles from './MoreInfoTooltip.module.scss';
import { DLSSize } from 'src/types/dls-size.enum';
import layout from 'src/styles/layout.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import Selectors from 'src/utils/selectors';

export interface MoreInfoTooltipProps {
    width?: string;
    children?: JSX.Element;
    className?: string;
    placement?: TooltipPlacement;
    title?: string;
    body: string | JSX.Element;
    linkName?: string | JSX.Element;
    link?: string;
    internalLink?: boolean;
    buttonAriaLabel: string;
    onClick?: Function;
    iconSize?: DLSSize;
    id?: string;
}

export default function MoreInfoTooltip({
    width,
    children,
    className,
    placement,
    title,
    body,
    linkName,
    link,
    internalLink,
    buttonAriaLabel,
    onClick,
    iconSize,
    id
}: MoreInfoTooltipProps): JSX.Element {
    const maxWidth: string = width || '300px';
    const ariaLabelledById: string = id || buttonAriaLabel.replace(/\s/g, '');

    return (
        <Tooltip
            theme={{
                '[role=tooltip]': {
                    maxWidth
                }
            }}
            triggerElement={
                <TooltipButton
                    info
                    onClick={onClick}
                    icon={children || <GlyphInfo size={iconSize || DLSSize.LARGE} className={styles.infoIcon} />}
                />
            }
            className={`${className} ${styles.moreInfoTooltip}`}
            info
            placement={placement}
            data-cy={Selectors.General.MORE_INFO_TOOLTIP}
            aria-labelledby={ariaLabelledById}
            id={ariaLabelledById + '-tooltip'}
            // Override DLS
            // This key prop is added to remount the Tooltip when placement changes since DLS do not update it correctly
            key={placement}
        >
            {title && (
                <Typography component="p" variant={TypographyVariants.HEADING_XS} key="title" className="title">
                    {title}
                </Typography>
            )}
            <div className={`body-1 ${styles.bodyText}`}>{typeof body === 'string' ? <p>{body}</p> : body}</div>
            {linkName && link && (
                <Anchor
                    href={link}
                    typographyProps={{ component: 'div', variant: TypographyVariants.SUBTEXT, className: 'link' }}
                >
                    {linkName} {!internalLink && <GlyphLinkOut />}
                </Anchor>
            )}
            <span id={ariaLabelledById} className={layout.screenReaderOnly}>
                {buttonAriaLabel} {LiteralsService.getLiteral('Tooltips.moreInfo')}
            </span>
        </Tooltip>
    );
}
